import { useQuery } from "@tanstack/react-query";
import ContainerHeaderSection from "@/reports/teacher/common/ContainerHeaderSection";
import OverviewDataResults from "@/reports/teacher/common/OverviewDataResults";
import useInsightsSharedContext from "@/reports/teacher/utils/useInsightsSharedContext/useInsightsSharedContext";
import useReadingLevelGet from "@/assignments/useReadingLevelGet";
import { classroomReadingLevelQueries } from "@/reports/queries/classroomReadingLevel";
import { studentReadingLevelQueries } from "@/reports/queries/studentReadingLevel";
import EmptyStateBanner from "../EmptyStateBanner";
export interface OverViewContainerProps {
  title?: string;
  explanationText?: string;
  isClassroomPage: boolean;
  hasNoActiveClassrooms: boolean;
  hasNoStudentsInClassroom: boolean;
  studentFirstName?: string;
}
const hasNoActiveClassroomsMessage =
  "There are no active classrooms available. You can add new classes or sync classes from your LMS in class directory.";
const hasNoStudentsInClassroomMessage =
  "This classroom has no students. You can add students to your Newsela class or sync classes from your LMS in class directory.";
const emptyStateDefaultMessage =
  "Create an assignment for your students or encourage them to read independently and view their progress on this page.";
const emptyStateButtonText = "Go to class directory";
const emptyStateButtonHref = "/account/classes/";
const OverViewContainer = ({
  title,
  explanationText,
  isClassroomPage = false,
  hasNoActiveClassrooms,
  hasNoStudentsInClassroom,
  studentFirstName,
}: OverViewContainerProps) => {
  const { selectedFilters } = useInsightsSharedContext();
  const { classroomUniqueId, dateRange, studentUniqueId, onlyAssignedContent } =
    selectedFilters;
  const minDate = dateRange[0];
  const maxDate = dateRange[1];

  const skipApiCall = hasNoActiveClassrooms || hasNoStudentsInClassroom;

  const onlyAssignedContentFilter =
    onlyAssignedContent !== null
      ? {
          only_assigned_content: onlyAssignedContent,
        }
      : {};

  const enableClassroomReadingLevelQuery =
    isClassroomPage && !skipApiCall && !!classroomUniqueId;

  const {
    data: classroomReadingLevelData,
    isLoading: classroomReadingLevelLoading,
    isError: classroomReadingLevelError,
  } = useQuery(
    classroomReadingLevelQueries.filter(
      {
        min_date: minDate,
        max_date: maxDate,
        entity_id: classroomUniqueId ?? "",
        ...onlyAssignedContentFilter,
      },
      enableClassroomReadingLevelQuery,
    ),
  );

  const enableStudentReadingLevelQuery =
    !isClassroomPage && !skipApiCall && !!studentUniqueId;

  const {
    data: studentReadingLevelData,
    isLoading: studentReadingLevelLoading,
    isError: studentReadingLevelError,
  } = useQuery(
    studentReadingLevelQueries.filter(
      {
        min_date: minDate,
        max_date: maxDate,
        entity_id: studentUniqueId ?? "",
        ...onlyAssignedContentFilter,
      },
      enableStudentReadingLevelQuery,
    ),
  );

  const entityReadingLevelData =
    studentReadingLevelData ?? classroomReadingLevelData;
  const entityReadingLevel = entityReadingLevelData
    ? entityReadingLevelData.data.data
    : null;
  const isLoading = studentReadingLevelLoading || classroomReadingLevelLoading;
  const isError = studentReadingLevelError || classroomReadingLevelError;

  const entityHasNoOverviewActivity =
    entityReadingLevel?.articles_viewed_count === 0;

  const isEmptyOverviewState = skipApiCall || entityHasNoOverviewActivity;

  const emptyStateMessage = hasNoActiveClassrooms
    ? hasNoActiveClassroomsMessage
    : hasNoStudentsInClassroom
      ? hasNoStudentsInClassroomMessage
      : emptyStateDefaultMessage;

  const {
    data: legacyStudentReadingLevel,
    loading: legacyStudentReadingLevelLoading,
  } = useReadingLevelGet(
    {
      user_id: studentUniqueId,
      source: "nwea",
    },
    isClassroomPage,
  );

  const nweaScore = legacyStudentReadingLevel?.value;

  return (
    <div>
      {isEmptyOverviewState ? (
        <EmptyStateBanner
          emptyStateMessage={emptyStateMessage}
          emptyStateButtonText={emptyStateButtonText}
          emptyStateButtonHref={emptyStateButtonHref}
          bannerHasCta={
            !entityHasNoOverviewActivity || hasNoStudentsInClassroom
          }
        />
      ) : null}
      <ContainerHeaderSection title={title} explanationText={explanationText} />
      <OverviewDataResults
        data={entityReadingLevel}
        isClassroomPage={isClassroomPage}
        isEmptyOverviewState={isEmptyOverviewState}
        loading={isLoading}
        error={isError}
        studentFirstName={studentFirstName as string}
        nweaScore={nweaScore}
        nweaScoreLoading={legacyStudentReadingLevelLoading}
      />
    </div>
  );
};

export default OverViewContainer;
